import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeader, parseResponse } from '../api/helpers';
import { inputStyle, labelStyle, modalWrap, primaryBtn, cancelBtn, successAlert, errorAlert } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';

const PLAN_COLORS = {
  basic:      { bg: 'rgba(100,116,139,0.15)', text: '#94a3b8' },
  pro:        { bg: 'rgba(14,165,233,0.15)',  text: '#38bdf8' },
  enterprise: { bg: 'rgba(245,158,11,0.15)',  text: '#fbbf24' },
};

function HospitalModal({ onClose, onSuccess }) {
  const [form, setForm]       = useState({ name: '', address: '', admin_email: '', plan: 'basic' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const headers = await getAuthHeader();
      onSuccess(parseResponse(await axios.post(`${API_URL}/hospitals`, form, { headers })));
    } catch { setError('Failed to create hospital'); }
    finally { setLoading(false); }
  };

  const selectStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', fontSize: 13, outline: 'none', color: 'var(--text-primary)', background: 'rgba(255,255,255,0.06)', boxSizing: 'border-box' };

  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Add New Hospital</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20 }}>✕</span>
        </div>
        {error && <div style={errorAlert}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>Hospital Name</label><input style={inputStyle} placeholder="e.g. City Care Hospital" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>Address</label><input style={inputStyle} placeholder="e.g. Hyderabad, Telangana" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>Admin Email</label><input style={inputStyle} type="email" placeholder="admin@hospital.com" value={form.admin_email} onChange={e => setForm({...form, admin_email: e.target.value})} /></div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Plan</label>
            <select style={selectStyle} value={form.plan} onChange={e => setForm({...form, plan: e.target.value})}>
              <option value="basic">Basic</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, ...cancelBtn }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, ...primaryBtn(loading) }}>{loading ? 'Creating…' : 'Create Hospital'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ViewHospitalModal({ hospital, onClose }) {
  const pc = PLAN_COLORS[hospital.plan] || PLAN_COLORS.basic;
  const details = [
    { label: 'Name', value: hospital.name },
    { label: 'Plan', value: hospital.plan },
    { label: 'Address', value: hospital.address || '—' },
    { label: 'Admin Email', value: hospital.admin_email || '—' },
    { label: 'Max Staff', value: hospital.max_staff },
    { label: 'Status', value: hospital.is_active ? 'Active' : 'Inactive' },
    { label: 'Created', value: hospital.created_at?.slice(0, 10) },
  ];

  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Hospital Details</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20 }}>✕</span>
        </div>

        <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Hospital ID</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-light)', fontFamily: 'monospace' }}>{hospital.hospital_id}</div>
          </div>
          <span style={{ background: pc.bg, color: pc.text, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{hospital.plan}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
          {details.map((item, i) => (
            <div key={i} style={{ background: 'var(--bg-card-hover)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.value}</div>
            </div>
          ))}
        </div>

        <button onClick={onClose} style={{ width: '100%', ...cancelBtn }}>Close</button>
      </div>
    </div>
  );
}

export default function Hospitals() {
  const [hospitals, setHospitals]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [showCreate, setShowCreate]     = useState(false);
  const [viewHospital, setViewHospital] = useState(null);
  const [successMsg, setSuccessMsg]     = useState('');
  const [error, setError]               = useState('');

  useEffect(() => { fetchHospitals(); }, []);

  const fetchHospitals = async () => {
    setLoading(true);
    try { const headers = await getAuthHeader(); setHospitals(parseResponse(await axios.get(`${API_URL}/hospitals`, { headers })).hospitals || []); }
    catch { setError('Failed to load hospitals'); }
    finally { setLoading(false); }
  };

  const handleCreateSuccess = (data) => {
    setShowCreate(false);
    setSuccessMsg(`Hospital created! ID: ${data.hospital_id || data.hospital?.hospital_id || 'created'}`);
    fetchHospitals();
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const filtered = hospitals.filter(h =>
    h.name?.toLowerCase().includes(search.toLowerCase()) ||
    h.hospital_id?.includes(search) || h.admin_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <input placeholder="Search by name, ID or email…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 220, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--input-border)', background: 'var(--bg-input)', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }} />
        <button onClick={() => setShowCreate(true)} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 12px rgba(99,102,241,0.4)', whiteSpace: 'nowrap' }}>
          + Add Hospital
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Hospitals',    value: hospitals.length,                                  grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
          { label: 'Active',             value: hospitals.filter(h => h.is_active).length,        grad: 'linear-gradient(135deg,#22c55e,#16a34a)' },
          { label: 'Pro / Enterprise',   value: hospitals.filter(h => h.plan !== 'basic').length, grad: 'linear-gradient(135deg,#f59e0b,#f97316)' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.grad, borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, marginBottom: 6, marginTop: 8 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {successMsg && <div style={successAlert}>{successMsg}</div>}
      {error && <div style={errorAlert}>{error}</div>}

      <div className="page-card">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>Loading hospitals…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>{search ? 'No hospitals found' : 'No hospitals added yet'}</div>
        ) : (
          <table className="dark-table">
            <thead><tr>{['Hospital ID','Name','Admin Email','Plan','Max Staff','Status','Created','Actions'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.map(h => {
                const pc = PLAN_COLORS[h.plan] || PLAN_COLORS.basic;
                return (
                  <tr key={h.hospital_id}>
                    <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--accent-light)', fontWeight: 600 }}>{h.hospital_id}</td>
                    <td style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{h.name}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{h.admin_email || '—'}</td>
                    <td><span style={{ background: pc.bg, color: pc.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{h.plan}</span></td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{h.max_staff}</td>
                    <td><span style={{ background: h.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: h.is_active ? '#4ade80' : '#f87171', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{h.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{h.created_at?.slice(0, 10)}</td>
                    <td><button onClick={() => setViewHospital(h)} style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 7, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: 'var(--accent-light)', fontWeight: 600 }}>View</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate   && <HospitalModal onClose={() => setShowCreate(false)} onSuccess={handleCreateSuccess} />}
      {viewHospital && <ViewHospitalModal hospital={viewHospital} onClose={() => setViewHospital(null)} />}
    </div>
  );
}
